import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  MapPin, Phone, Clock, Check, ChevronLeft, 
  Utensils, Car, Package, Home, RefreshCw, Wifi, WifiOff, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Navbar } from '../components/Navbar';
import { toast } from 'sonner';
import { OrderChat } from '../components/OrderChat';

export default function OrderTrackingPage() {
  const { orderId } = useParams();
  const { api } = useAuth();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const restaurantMarkerRef = useRef(null);
  const customerMarkerRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!orderId) return;
    
    // Determine WebSocket URL from backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/order/${orderId}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
        console.log('WebSocket connected for order tracking');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'status_update') {
            // Update tracking status
            setTracking(prev => prev ? {
              ...prev,
              status: data.status,
              driver: prev.driver ? {
                ...prev.driver,
                location: data.driver_location
              } : null
            } : null);
            toast.info(`Order status: ${data.status.replace('_', ' ')}`);
          }
          
          if (data.type === 'location_update' && data.driver_location) {
            // Update driver location on map
            setTracking(prev => prev ? {
              ...prev,
              driver: prev.driver ? {
                ...prev.driver,
                location: {
                  latitude: data.driver_location.latitude,
                  longitude: data.driver_location.longitude,
                  updated_at: data.driver_location.updated_at
                }
              } : null
            } : null);
          }
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        console.log('WebSocket disconnected');
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (e) {
      console.error('WebSocket connection error:', e);
    }
  }, [orderId]);

  // Send heartbeat
  useEffect(() => {
    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
    
    return () => clearInterval(heartbeatInterval);
  }, []);

  // Connect WebSocket on mount
  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  const fetchTracking = useCallback(async () => {
    try {
      const response = await api.get(`/orders/${orderId}/tracking`);
      setTracking(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load tracking info');
    } finally {
      setLoading(false);
    }
  }, [api, orderId]);

  useEffect(() => {
    fetchTracking();
  }, [fetchTracking]);

  // Initialize Google Map
  useEffect(() => {
    const initMap = () => {
      if (!window.google || !mapRef.current) return;
      
      googleMapRef.current = new window.google.maps.Map(mapRef.current, {
        center: { lat: 51.5074, lng: -0.1278 },
        zoom: 14,
        styles: [
          { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }
        ],
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false
      });
    };

    if (window.google && window.google.maps) {
      initMap();
    } else {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (apiKey) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initMap;
        document.head.appendChild(script);
      }
    }
  }, []);

  // Update markers when tracking data changes
  useEffect(() => {
    if (!googleMapRef.current || !window.google || !tracking) return;

    const bounds = new window.google.maps.LatLngBounds();

    // Restaurant marker
    if (tracking.restaurant?.latitude && tracking.restaurant?.longitude) {
      if (restaurantMarkerRef.current) {
        restaurantMarkerRef.current.setMap(null);
      }
      
      restaurantMarkerRef.current = new window.google.maps.Marker({
        position: {
          lat: tracking.restaurant.latitude,
          lng: tracking.restaurant.longitude
        },
        map: googleMapRef.current,
        title: tracking.restaurant.name,
        icon: {
          url: 'data:image/svg+xml,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="18" fill="#f97316" stroke="white" stroke-width="3"/>
              <text x="20" y="26" text-anchor="middle" fill="white" font-size="16">🍽</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(40, 40)
        }
      });
      bounds.extend(restaurantMarkerRef.current.getPosition());
    }

    // Driver marker
    if (tracking.driver?.location) {
      if (driverMarkerRef.current) {
        driverMarkerRef.current.setMap(null);
      }
      
      driverMarkerRef.current = new window.google.maps.Marker({
        position: {
          lat: tracking.driver.location.latitude,
          lng: tracking.driver.location.longitude
        },
        map: googleMapRef.current,
        title: tracking.driver.name,
        icon: {
          url: 'data:image/svg+xml,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="18" fill="#22c55e" stroke="white" stroke-width="3"/>
              <text x="20" y="26" text-anchor="middle" fill="white" font-size="16">🚗</text>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(40, 40)
        }
      });
      bounds.extend(driverMarkerRef.current.getPosition());
    }

    // Fit bounds if we have markers
    if (!bounds.isEmpty()) {
      googleMapRef.current.fitBounds(bounds);
      
      // Don't zoom too far
      const listener = googleMapRef.current.addListener('idle', () => {
        if (googleMapRef.current.getZoom() > 16) {
          googleMapRef.current.setZoom(16);
        }
        window.google.maps.event.removeListener(listener);
      });
    }
  }, [tracking]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'placed': return Package;
      case 'confirmed': return Check;
      case 'preparing': return Utensils;
      case 'ready': return Package;
      case 'picked_up': return Car;
      case 'in_transit': return Car;
      case 'delivered': return Home;
      default: return Package;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Link to="/orders">
            <Button variant="outline">Back to Orders</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-slate-50 pt-16">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link to="/orders">
                <Button variant="ghost" size="sm">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Orders
                </Button>
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-900">
                    Order #{orderId?.slice(-6)}
                  </h1>
                  {wsConnected ? (
                    <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      <Wifi className="w-3 h-3" />
                      Live
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                      <WifiOff className="w-3 h-3" />
                      Offline
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500">
                  {tracking?.estimated_delivery && `Estimated: ${tracking.estimated_delivery}`}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={fetchTracking}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Map */}
            <Card className="overflow-hidden">
              <div ref={mapRef} className="h-[350px] bg-slate-100">
                {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    <div className="text-center">
                      <MapPin className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>Map unavailable</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Legend */}
              <CardContent className="py-3">
                <div className="flex items-center justify-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-orange-500" />
                    <span className="text-slate-600">Restaurant</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-green-500" />
                    <span className="text-slate-600">Driver</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Details */}
            <div className="space-y-4">
              {/* Status */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-slate-900">Order Status</h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${
                      tracking?.status === 'delivered' ? 'bg-green-100 text-green-700' :
                      tracking?.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {tracking?.status?.replace('_', ' ')}
                    </span>
                  </div>

                  {/* Timeline */}
                  <div className="space-y-3">
                    {tracking?.timeline?.map((step, index) => {
                      const Icon = getStatusIcon(step.status);
                      return (
                        <div key={step.status} className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            step.completed ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'
                          }`}>
                            {step.completed ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Icon className="w-4 h-4" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium ${step.completed ? 'text-slate-900' : 'text-slate-400'}`}>
                              {step.label}
                            </p>
                            {step.timestamp && (
                              <p className="text-xs text-slate-500">
                                {new Date(step.timestamp).toLocaleTimeString()}
                              </p>
                            )}
                          </div>
                          {index < tracking.timeline.length - 1 && (
                            <div className={`absolute left-4 mt-8 w-0.5 h-4 ${
                              step.completed ? 'bg-green-200' : 'bg-slate-200'
                            }`} style={{ marginLeft: '14px' }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Restaurant Info */}
              {tracking?.restaurant && (
                <Card>
                  <CardContent className="p-4">
                    <h2 className="font-semibold text-slate-900 mb-3">Restaurant</h2>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Utensils className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{tracking.restaurant.name}</p>
                        <p className="text-sm text-slate-500">{tracking.restaurant.address}</p>
                        {tracking.restaurant.phone && (
                          <a href={`tel:${tracking.restaurant.phone}`} className="text-sm text-orange-600 flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {tracking.restaurant.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Driver Info */}
              {tracking?.driver && (
                <Card>
                  <CardContent className="p-4">
                    <h2 className="font-semibold text-slate-900 mb-3">Your Driver</h2>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Car className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{tracking.driver.name}</p>
                        <p className="text-sm text-slate-500 capitalize">{tracking.driver.vehicle_type}</p>
                        {tracking.driver.phone && (
                          <a href={`tel:${tracking.driver.phone}`} className="text-sm text-orange-600 flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {tracking.driver.phone}
                          </a>
                        )}
                      </div>
                      {tracking.driver.location && (
                        <div className="text-right text-xs text-slate-400">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Updated {new Date(tracking.driver.location.updated_at).toLocaleTimeString()}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Delivery Address */}
              {tracking?.delivery_address && (
                <Card>
                  <CardContent className="p-4">
                    <h2 className="font-semibold text-slate-900 mb-3">Delivery Address</h2>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Home className="w-5 h-5 text-blue-600" />
                      </div>
                      <p className="text-slate-700">{tracking.delivery_address}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Chat with Driver/Restaurant */}
              <Card>
                <CardContent className="p-4">
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => setShowChat(true)}
                    data-testid="open-chat-button"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Chat with Restaurant/Driver
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Order Chat Modal */}
      <OrderChat 
        orderId={orderId}
        isOpen={showChat}
        onClose={() => setShowChat(false)}
        participants={{ customer: true, restaurant: true, driver: tracking?.driver ? true : false }}
      />
    </div>
  );
}
